import { FC, useMemo, useState } from 'react';
import { useStoreLocatorContext } from './context';
import {
  Alert,
  Button,
  Icon,
  IconSvg,
  Input,
  Spinner,
  TreezStore,
} from '@/components';
import { TreezStore as TreezStoreType } from '@/types';
import classNames from 'classnames';

import { CURRENT_LOCATION_LABEL } from './constants';
import { StoreExternalItem } from './StoreLocator.components';
import styles from './storelocator.module.scss';
import { debounce } from 'lodash';

interface IPickupSectionProps {
  showInstructionText: boolean;
  loadingLocation: boolean;
  handleUseCurrentLocation: () => void;
}

const PickupSection: FC<IPickupSectionProps> = ({
  showInstructionText,
  loadingLocation,
  handleUseCurrentLocation,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const {
    isPickup,
    isPickupInvalid,
    setIsPickupInvalid,
    singleStore,
    pickupSectionTitle,
    stores,
  } = useStoreLocatorContext();

  const handleChange = debounce(e => {
    setSearchTerm(e.target.value);
  }, 300);

  const filteredStores = useMemo(() => {
    const resultNameFilter = stores?.filter(store =>
      store?.name?.toLowerCase().includes(searchTerm?.toLowerCase()),
    );
    return resultNameFilter;
  }, [stores, searchTerm]);

  return (
    <div className={styles.pickup_section}>
      <Button
        ariaLabel={CURRENT_LOCATION_LABEL}
        className={classNames(styles.current_location, {
          [styles.hidden]: singleStore && isPickup,
        })}
        onClick={handleUseCurrentLocation}
      >
        {loadingLocation ? (
          <Spinner className={styles.crosshairs_icon} sm />
        ) : (
          <Icon className={styles.crosshairs_icon} name="crosshairs" />
        )}
        <span>{CURRENT_LOCATION_LABEL}</span>
      </Button>
      {showInstructionText && (
        <p className={styles.pickup_section__title}>{pickupSectionTitle}</p>
      )}
      <div
        className={classNames(
          'store_locator__search_box',
          'store_locator__search_box_filter_header',
          styles.search_input,
        )}
      >
        <Input
          autoComplete="off"
          className="store_locator__search_box_input"
          isSmall
          name={'searchBox2'}
          onChange={e => handleChange(e)}
          placeholder="Search"
          type="search"
        />
        <Button
          ariaLabel="Search"
          className="store_locator__search_box_button"
          color="primary"
          onClick={() => {
            setSearchTerm(searchTerm);
          }}
        >
          <IconSvg name="search" />
        </Button>
      </div>
      <div className={styles.pickup_section__content}>
        {isPickup && isPickupInvalid && (
          <Alert
            error
            isOpen
            toggle={event => {
              event.stopPropagation();
              setIsPickupInvalid(false);
            }}
          >
            Please select a shop.
          </Alert>
        )}
        <StoresList
          filteredStores={filteredStores}
          loadingLocation={loadingLocation}
        />
      </div>
    </div>
  );
};

interface IStoresListProps {
  loadingLocation: boolean;
  filteredStores: TreezStoreType[];
}

const StoresList: FC<IStoresListProps> = ({
  loadingLocation,
  filteredStores,
}) => {
  const { selectedStoreId, setSelectedStoreId, externalStores } =
    useStoreLocatorContext();

  return (
    <fieldset>
      {filteredStores?.map(store => {
        const isActive = selectedStoreId === store.shortName;

        return (
          <TreezStore
            className={styles.store}
            distanceInMiles={store?.distance ?? ''}
            isActive={isActive}
            isLoadingLocation={loadingLocation}
            isRadioType
            key={store?.shortName}
            onSelected={setSelectedStoreId}
            store={store}
            withBorders={false}
          />
        );
      })}
      {externalStores?.map(store => (
        <StoreExternalItem
          isOpen={true}
          isSelected={selectedStoreId === store?.shortName}
          key={store?.shortName}
          onSelected={setSelectedStoreId}
          store={store}
        />
      ))}
    </fieldset>
  );
};

export { PickupSection };
